import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, Location } from '@angular/common';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { FormlyModule } from '@ngx-formly/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { GenAiModel } from '@parashift/shared/models';
import { ErrorResponse, GenAiModelService, NotificationService, Logger, GenAiModelsRegister, SessionService } from '@parashift/shared/services';
import { ModalService } from '@parashift/shared/components';
import { HeaderActionStore } from '@parashift/shared/stores';
import { AdminGenAiModelsEditFormComponent } from '../admin-gen-ai-models/admin-gen-ai-models-edit-form/admin-gen-ai-models-edit-form.component';
import { GenAiModelsEditFormComponent } from './gen-ai-models-edit-form/gen-ai-models-edit-form.component';

@Component({
  selector: 'gen-ai-models-edit',
  templateUrl: './gen-ai-models-edit.component.html',
  imports: [
    AdminGenAiModelsEditFormComponent,
    AsyncPipe,
    FaIconComponent,
    FormlyModule,
    GenAiModelsEditFormComponent,
    ReactiveFormsModule
  ]
})
export class GenAiModelsEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private genAiModelService = inject(GenAiModelService);
  private genAiModelsRegister = inject(GenAiModelsRegister);
  private modalService = inject(ModalService);
  private location = inject(Location);
  private notificationService = inject(NotificationService);
  private headerActionStore = inject(HeaderActionStore);
  private sessionService = inject(SessionService);
  private logger = inject(Logger);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  @ViewChild(GenAiModelsEditFormComponent, { static: false }) private formComponent: GenAiModelsEditFormComponent;
  @ViewChild(AdminGenAiModelsEditFormComponent, { static: false }) private adminFormComponent: AdminGenAiModelsEditFormComponent;

  get isEngineering(): boolean {
    return this.sessionService.userHasRole('engineering') || this.sessionService.userHasRole('admin');
  }

  protected _model$ = new BehaviorSubject<GenAiModel>(undefined);
  readonly model$ = this._model$.asObservable();
  get model() { return this._model$.getValue(); }

  id: string;
  genAiModel: GenAiModel;
  error: ErrorResponse;
  disableForm = false;
  tenant_id: number;
  useAdminForm = false;

  constructor() {
    const destroyRef = this.destroyRef;
    destroyRef.onDestroy(() => this.headerActionStore.destroy());
  }

  ngOnInit() {
    this.useAdminForm = false;
    this.headerActionStore.addActions({ backButton: { dispatch: () => this.goToListView() }});

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
      this.tenant_id = Number(params.get('tenant_id'));
      this.id = params.get('id');

      this.headerActionStore.addActions({
        saveButton: { dispatch: () => this.genAiModel && (this.useAdminForm ? this.adminFormComponent?.submit() : this.formComponent?.submit()) },
        menu: {
          items: []
        }
      });

      if (this.id !== 'new') {
        this.getGenAiModel(params.get('id'));
      } else {
        this.initGenAiModel();
      }
    });
  }

  getGenAiModel(id: string) {
    this.genAiModelService.findRecord(id, undefined, this.tenant_id)
      .subscribe({
        next: (genAiModel: GenAiModel) => {
          if (!this.isEngineering && (!genAiModel.tenant_id || genAiModel.tenant_id !== this.tenant_id)) {
            this.goToDetailView();
          } else {
            this.setGenAiModel(genAiModel);
          }
        },
        error: (error: ErrorResponse) => {
          this.id = null;
          this.error = error;
          this.logger.log('gen-ai-model-edit-component', 'GEN AI MODEL Error', error);
        }
      });
  }

  setGenAiModel(genAiModel: GenAiModel) {
    this.addDeleteButton();
    this.genAiModel = genAiModel;
    this.id = genAiModel.id;
    this._model$.next(this.genAiModel);
  }

  initGenAiModel() {
    this.genAiModel = new GenAiModel({
      tenant_id: this.tenant_id,
      name: '',
      identifier: '',
      live: !this.useAdminForm
    });

    this._model$.next(this.genAiModel);
    this.headerActionStore.addActions({ saveButton: { dispatch: () => this.genAiModel && (this.useAdminForm ? this.adminFormComponent?.submit() : this.formComponent?.submit()) }});
  }

  saveGenAiModel(model: GenAiModel) {
    this.disableForm = true;
    this.genAiModel.plainModel = model.plainModel;

    this.genAiModelService.saveRecord(this.genAiModel, this.tenant_id).subscribe({
      next: (genAiModel: GenAiModel) => {
        this.genAiModel = genAiModel;
        genAiModel.live && this.genAiModelsRegister.addOrReplaceModel(genAiModel);
        !genAiModel.live && this.genAiModelsRegister.removeModel(genAiModel);
        this._model$.next(this.genAiModel);

        this.notificationService.success({
          description: $localize `:@@components.gen_ai_models_edit.model_save_success:The GenAI Model has been successfully saved.`,
          title: $localize `:@@components.gen_ai_models_edit.model_saved:GenAI Model saved`
        });

        if (this.id === 'new') {
          this.id = this.genAiModel.id = genAiModel.id;
          this.createAndReplaceUrl();
        }
        this.addDeleteButton();
        this.disableForm = false;
      },
      error: (error: ErrorResponse) => {
        this.logger.log('Save GenAI Model Error', error);
        this.notificationService.formattedErrorResponse(error);
        this.disableForm = false;
      }
    });
  }

  deleteGenAiModel() {
    if (!this.id || this.id === 'new') {
      return;
    }

    this.modalService
      .confirmation({
        initialState: {
          title: $localize `:@@components.gen_ai_models_edit.delete_model:Delete GenAI Model`,
          description: $localize `:@@components.gen_ai_models_edit.delete_model_confirm:Do you want to delete this GenAI Model?`
        }
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.genAiModelService.deleteRecord(this.id, this.tenant_id).subscribe({
          next: () => {
            this.notificationService.success({
              description: $localize `:@@components.gen_ai_models_edit.model_delete_success:The GenAiModel has been successfully deleted.`,
              title: $localize `:@@components.gen_ai_models_edit.model_deleted:GenAI Model deleted`
            });
            this.genAiModelsRegister.removeModel(this.genAiModel);
            this.goToListView();
          },
          error: (error: ErrorResponse) => {
            this.notificationService.formattedErrorResponse(error);
          }
        });
      });
  }

  createAndReplaceUrl() {
    const url = this.router.createUrlTree(['../../', this.id, 'edit'], { relativeTo: this.route }).toString();
    this.location.replaceState(url);
    this.headerActionStore.replaceId(this.id);
  }

  goToListView() {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }

  private goToDetailView() {
    this.router.navigate(['../', 'detail'], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  private addDeleteButton() {
    this.headerActionStore.addMenuItems([{
      dispatch: () => this.genAiModel && this.deleteGenAiModel(),
      identifier: 'delete'
    }]);
  }
}
